/**
 * The pair page's `?maker=` parameter, as a decision rather than a side effect.
 *
 * This exists because writing the URL unconditionally was costing two requests
 * on every pair open. `browserHistory.replace` always builds a fresh `Location`
 * and `routerLocationAtom` is compared by reference, so a replace that changed
 * nothing still notified every subscriber — including the router sync, which
 * re-ran the network sync and issued a second `POST /user/change-network`.
 *
 * Returning `undefined` for "nothing to do" keeps that judgement here, where it
 * can be read and tested, instead of inside a reaction that also talks to the
 * router.
 */

/**
 * The search string the URL should carry, or `undefined` when it already
 * carries it and the caller should not write.
 *
 * @param search the current location's search, with or without its leading `?`
 * @param maker the wallet the trades table is filtered by, if any
 */
export const nextMakerSearch = (
  search: string,
  maker: string | undefined,
): string | undefined => {
  const params = new URLSearchParams(search)

  if (maker) {
    params.set('maker', maker)
  } else {
    params.delete('maker')
  }

  const next = params.toString()
  // `URLSearchParams` takes a leading `?` but never emits one, so the two
  // sides have to be compared in the same shape or every call looks changed.
  return next === search.replace(/^\?/, '') ? undefined : next
}
